import React, { useState } from "react";
import { formatMoney } from "../utils/money";

/**
 * Shared "full product specs" modal.
 *
 * Order specs and product images live on every order payload, but each screen
 * used to surface a different slice of them (Inbound showed size + colour only,
 * Design Studio showed three lines, the buyer workflow showed none). This is the
 * single place that renders all of it, so every screen shows the same sheet.
 *
 * Pass the order row and, when the payload carries it, the linked catalog
 * product — the catalog block is skipped when absent.
 */

const IMAGE_BASE = process.env.REACT_APP_IMAGE_URL || "";

/**
 * Financial fields (order price, quotation price, final/buyer price, the
 * supplier's own quote and the admin's counter-quote) are internal margin
 * data. Only a full admin may see them — suppliers AND every sub-admin
 * (graphic designers included; `sub_admin_id` is just a record id, not a
 * privilege tier) must not.
 */
const viewerCanSeeFinancials = () => {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return String(u?.user_type || "").toLowerCase() === "admin";
  } catch {
    return false;
  }
};

// Labels of caller-supplied extraFields that are money/quote data.
const isFinancialLabel = (label = "") => /price|quotation/i.test(label);

/** Prices are rupees; blank stays a dash rather than becoming "₹0". */
const money = (v) =>
  v === null || v === undefined || v === "" ? "—" : formatMoney(v);

/** Stored paths are relative; absolute URLs (S3/CDN) are used as-is. */
export const resolveSrc = (p) =>
  !p ? "" : String(p).startsWith("http") ? p : `${IMAGE_BASE}${p}`;

// `product_image` is a JSON array string ('["/uploads/products/x.jpg"]'), but
// older rows hold a bare path or a comma-joined list.
export const parseProductImages = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean).map(String);

  const text = String(raw).trim();
  if (!text || text === "null" || text === "[]") return [];

  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String);
    if (typeof parsed === "string" && parsed) return [parsed];
    return [];
  } catch {
    return text
      .replace(/^\[|\]$/g, "")
      .split(",")
      .map((part) => part.replace(/["']/g, "").trim())
      .filter(Boolean);
  }
};

/**
 * Spec columns are stored JSON-encoded, and inconsistently so: `size` is
 * '"10x10x10"' on some rows and '"[\"10x10x10\"]"' (encoded twice) on others.
 * Rendering the column directly printed the quotes and brackets. Unwrap as many
 * encoding layers as are actually there, then join arrays.
 */
export const displayValue = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";

  let text = String(v).trim();
  if (!text || text === "null" || text === "undefined") return "—";

  // Bounded loop: each pass peels one layer; 4 is far more than any real row.
  for (let i = 0; i < 4; i += 1) {
    if (!text.startsWith("[") && !text.startsWith('"')) break;
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        return parsed.length ? parsed.filter(Boolean).join(", ") : "—";
      }
      if (typeof parsed !== "string") return String(parsed);
      text = parsed.trim();
      if (!text) return "—";
    } catch {
      break;
    }
  }
  return text;
};

// Product/order descriptions are stored as CKEditor HTML (`<p>…<br>…</p>`).
// This modal shows plain-text fields, not rendered HTML, so the raw markup
// was showing up as literal tag text instead of readable content.
const stripHtml = (html) => {
  const text = displayValue(html);
  if (text === "—") return text;
  return text
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim() || "—";
};

/**
 * Overlapping thumbnail stack — the standard way product images are shown in
 * every admin table. Renders the real artwork, not a placeholder icon.
 */
export const ProductImageStack = ({
  images,
  onOpen,
  max = 3,
  title = "View product images",
  emptyLabel = "No image",
  // Opt-in circular thumbnails (matching the round avatars used for this
  // same data on the storefront's orders page) — default stays the
  // existing rounded-square look everywhere else already using this.
  circular = false,
}) => {
  const list = Array.isArray(images) ? images : parseProductImages(images);
  if (list.length === 0)
    return <span className="al-muted">{emptyLabel}</span>;

  return (
    <button
      type="button"
      className={`al-imgstack${circular ? " al-imgstack--circular" : ""}`}
      title={title}
      onClick={(e) => {
        e.stopPropagation();
        if (onOpen) onOpen(list);
      }}
    >
      {list.slice(0, max).map((im, i) => (
        <span className="al-imgstack__i" key={i}>
          <img src={resolveSrc(im)} alt="" />
        </span>
      ))}
      {list.length > max && (
        <span className="al-imgstack__n">+{list.length - max}</span>
      )}
    </button>
  );
};

/** Lightbox for the image stack — separate from the specs sheet. */
export const ProductImagesModal = ({ open, onClose, images, title }) => {
  const list = Array.isArray(images) ? images : parseProductImages(images);
  const [active, setActive] = useState(0);

  if (!open || list.length === 0) return null;
  const current = resolveSrc(list[Math.min(active, list.length - 1)]);

  return (
    <div className="al-modal" onClick={onClose}>
      <div className="al-modal__scrim" />
      <div
        className="al-modal__box"
        onClick={(e) => e.stopPropagation()}
        style={{ width: "min(680px,94vw)" }}
      >
        <div className="al-modal__head">
          <div style={{ minWidth: 0 }}>
            <h3 className="al-modal__title">{title || "Product Images"}</h3>
            <p className="al-modal__sub">
              {list.length} {list.length === 1 ? "image" : "images"}
            </p>
          </div>
          <button type="button" className="al-act" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>

        <div style={{ padding: 20 }}>
          <div
            style={{
              borderRadius: 12,
              overflow: "hidden",
              border: "1px solid var(--color-border)",
              background: "var(--color-surface-2)",
              marginBottom: list.length > 1 ? 14 : 0,
            }}
          >
            <img
              src={current}
              alt=""
              style={{
                width: "100%",
                maxHeight: "56vh",
                objectFit: "contain",
                display: "block",
              }}
            />
          </div>

          {list.length > 1 && (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {list.map((im, i) => (
                <button
                  key={i}
                  type="button"
                  className="al-thumb"
                  style={{
                    width: 64,
                    height: 64,
                    cursor: "pointer",
                    padding: 0,
                    borderColor:
                      i === active ? "var(--color-primary)" : undefined,
                  }}
                  onClick={() => setActive(i)}
                >
                  <img src={resolveSrc(im)} alt="" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div style={{ minWidth: 0 }}>
    <div
      style={{
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: ".05em",
        textTransform: "uppercase",
        color: "var(--color-text-tertiary)",
        marginBottom: 3,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: 13,
        fontWeight: 600,
        color: "var(--color-text-primary)",
        wordBreak: "break-word",
      }}
    >
      {children}
    </div>
  </div>
);

const Section = ({ title, children, cols = 3 }) => (
  <div
    style={{
      padding: "16px 20px",
      borderBottom: "1px solid var(--color-border-soft)",
    }}
  >
    <div
      style={{
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: ".04em",
        textTransform: "uppercase",
        color: "var(--color-text-secondary)",
        marginBottom: 12,
      }}
    >
      {title}
    </div>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${cols},minmax(0,1fr))`,
        gap: 14,
      }}
    >
      {children}
    </div>
  </div>
);

const SPEC_FIELDS = [
  ["Material / Colour", "material_color"],
  ["Print Coverage", "print_coverage"],
  ["Size", "size"],
  ["Paper GSM", "paper_gsm"],
  ["Burst Factor", "burst_factor"],
  ["No. of Ply", "no_of_ply"],
  ["Lamination", "lamination"],
  ["Quantity", "quantity"],
];

const OrderSpecsModal = ({
  open,
  onClose,
  order,
  product,
  buyer,
  title,
  subtitle = "Full product & order specification",
  extraFields = [],
  // Buyer-uploaded reference images. Every caller already has this data on
  // the order row (as `mediaFiles` or `media_files`, an array of
  // { media_file: <path> }) — it just wasn't being rendered anywhere, only
  // counted ("User Media: 1") in an extra field with no way to actually see
  // the images. Pass explicitly to override, otherwise read off `order`.
  mediaFiles,
}) => {
  const [zoom, setZoom] = useState(null);
  const [refZoom, setRefZoom] = useState(null);

  if (!open || !order) return null;

  const images = parseProductImages(product?.product_image);
  const referenceImages = (
    mediaFiles ||
    order?.mediaFiles ||
    order?.media_files ||
    []
  )
    .map((f) => (typeof f === "string" ? f : f?.media_file))
    .filter(Boolean);
  // Order specs are authoritative, but fall back to the catalog product so a
  // blank order column still shows the value the buyer actually ordered from.
  const spec = (key) =>
    displayValue(
      order?.[key] !== null && order?.[key] !== undefined && order?.[key] !== ""
        ? order[key]
        : product?.[key]
    );

  const created = order?.created_at
    ? new Date(order.created_at).toLocaleString()
    : "—";

  const showFinancials = viewerCanSeeFinancials();
  const visibleExtraFields = showFinancials
    ? extraFields
    : extraFields.filter(({ label }) => !isFinancialLabel(label));

  return (
    <div className="al-modal" onClick={onClose}>
      <div className="al-modal__scrim" />
      <div
        className="al-modal__box"
        onClick={(e) => e.stopPropagation()}
        style={{ width: "min(760px,94vw)" }}
      >
        <div className="al-modal__head">
          <div style={{ minWidth: 0 }}>
            <h3 className="al-modal__title">
              {title ||
                `Order #${order.order_id ?? "—"} — ${
                  order.title || product?.title || "Untitled"
                }`}
            </h3>
            <p className="al-modal__sub">{subtitle}</p>
          </div>
          <button type="button" className="al-act" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>

        {/* Product images first — they are what most people open this for. */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--color-border-soft)" }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: ".04em",
              textTransform: "uppercase",
              color: "var(--color-text-secondary)",
              marginBottom: 12,
            }}
          >
            Product Images
          </div>
          {images.length === 0 ? (
            <span className="al-muted">No product images uploaded</span>
          ) : (
            <>
              {zoom && (
                <div
                  style={{
                    marginBottom: 12,
                    borderRadius: 12,
                    overflow: "hidden",
                    border: "1px solid var(--color-border)",
                    background: "var(--color-surface-2)",
                  }}
                >
                  <img
                    src={zoom}
                    alt=""
                    style={{ width: "100%", maxHeight: 320, objectFit: "contain", display: "block" }}
                  />
                </div>
              )}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {images.map((img, i) => {
                  const src = resolveSrc(img);
                  return (
                    <button
                      key={i}
                      type="button"
                      className="al-thumb"
                      style={{
                        width: 64,
                        height: 64,
                        cursor: "pointer",
                        padding: 0,
                        borderColor: zoom === src ? "var(--color-primary)" : undefined,
                      }}
                      title="Click to enlarge"
                      onClick={() => setZoom(zoom === src ? null : src)}
                    >
                      <img src={src} alt="" />
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Reference images/files the buyer attached to the order — these
            used to only show up as a bare count ("User Media: 1") with no
            way to actually view them. */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--color-border-soft)" }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: ".04em",
              textTransform: "uppercase",
              color: "var(--color-text-secondary)",
              marginBottom: 12,
            }}
          >
            Buyer Reference Images
          </div>
          {referenceImages.length === 0 ? (
            <span className="al-muted">No reference images uploaded</span>
          ) : (
            <>
              {refZoom && (
                <div
                  style={{
                    marginBottom: 12,
                    borderRadius: 12,
                    overflow: "hidden",
                    border: "1px solid var(--color-border)",
                    background: "var(--color-surface-2)",
                  }}
                >
                  <img
                    src={refZoom}
                    alt=""
                    style={{ width: "100%", maxHeight: 320, objectFit: "contain", display: "block" }}
                  />
                </div>
              )}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {referenceImages.map((img, i) => {
                  const src = resolveSrc(img);
                  return (
                    <button
                      key={i}
                      type="button"
                      className="al-thumb"
                      style={{
                        width: 64,
                        height: 64,
                        cursor: "pointer",
                        padding: 0,
                        borderColor: refZoom === src ? "var(--color-primary)" : undefined,
                      }}
                      title="Click to enlarge"
                      onClick={() => setRefZoom(refZoom === src ? null : src)}
                    >
                      <img src={src} alt="" />
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <Section title="Specifications">
          {SPEC_FIELDS.map(([label, key]) => (
            <Field key={key} label={label}>
              {spec(key)}
            </Field>
          ))}
        </Section>

        <Section title="Order">
          <Field label="Order ID">{displayValue(order.order_id)}</Field>
          <Field label="Order Stage">{displayValue(order.order_status)}</Field>
          <Field label="Order Type">{displayValue(order.order_type)}</Field>
          {showFinancials && (
            <>
              <Field label="Price">{money(order.price)}</Field>
              <Field label="Quotation Price">{money(order.quotation_price)}</Field>
              <Field label="Final Price">{money(order.final_price)}</Field>
            </>
          )}
          <Field label="Created">{created}</Field>
          {visibleExtraFields.map(({ label, value }) => (
            <Field key={label} label={label}>
              {displayValue(value)}
            </Field>
          ))}
        </Section>

        {product && (
          <Section title="Catalog Product">
            <Field label="Product Title">{displayValue(product.title)}</Field>
            {SPEC_FIELDS.filter(([, k]) => k !== "quantity").map(([label, key]) => (
              <Field key={key} label={label}>
                {displayValue(product[key])}
              </Field>
            ))}
          </Section>
        )}

        {buyer && (
          <Section title="Buyer">
            <Field label="Name">{displayValue(buyer.name)}</Field>
            <Field label="Email">{displayValue(buyer.email)}</Field>
            <Field label="Phone">{displayValue(buyer.phone)}</Field>
            {buyer.company_name && (
              <Field label="Company">{displayValue(buyer.company_name)}</Field>
            )}
          </Section>
        )}

        <Section title="Notes" cols={1}>
          <Field label="Product Description">
            {stripHtml(order.product_description || product?.description)}
          </Field>
          <Field label="Remark">{displayValue(order.remark)}</Field>
          <Field label="Quotation Remark">{displayValue(order.quotation_remark)}</Field>
        </Section>
      </div>
    </div>
  );
};

export default OrderSpecsModal;
